import React from 'react';
import { useNavigate } from 'react-router-dom';
import { StoreDateTime } from '../../../components/StoreDateTime';
import { Task } from '../../../types';
import { TaskNavigation } from './TaskNavigation';

interface TasksHeaderProps {
  tasks: Task[];
  storeName: string | null;
  imageId: string | null;
  storeTimeZone?: string;
  enabledMods: Set<string>;
  toggleMod: (tagName: string) => void;
  taskId?: string;
  chainId: string | null;
  isLoading: boolean;
}

export const TasksHeader: React.FC<TasksHeaderProps> = ({
  tasks,
  storeName,
  imageId,
  storeTimeZone,
  enabledMods,
  toggleMod,
  taskId,
  chainId,
  isLoading,
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  if (tasks.length === 0) {
    return null;
  }

  return (
    <div className="bg-white shadow-sm border-b rounded-lg p-4">
      <div className="space-y-3">
        <div className="flex justify-between">
          <div>
            {' '}
            <h1 className="text-xl font-bold mb-3">Task Detail</h1>
            <div className="text-gray-600 text-sm">
              {storeName && `Store: ${storeName}`}
              {imageId && ` • Image ID: ${imageId}`}
            </div>
          </div>
          <div className="flex items-start">
            <div className="flex gap-8 items-center">
              <div>
                <TaskNavigation
                  taskId={taskId}
                  chainId={chainId}
                  imageId={imageId}
                  isLoading={isLoading}
                />
              </div>
              <button
                onClick={handleBack}
                className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-md"
              >
                Back to Tasks
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Task ID
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mod
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Compliance Score
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  OSA Score
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Active
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tasks.map((task) => (
                <tr key={task.taskId} className="hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                    {task.taskId}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm">
                    <button
                      onClick={() => toggleMod(task.tagName)}
                      className={`px-2 py-1 rounded-md text-sm transition-colors ${
                        enabledMods.has(task.tagName)
                          ? 'bg-blue-100 text-gray-900'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {task.tagName}
                    </button>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">
                    <StoreDateTime value={task.taskCreatedAt} storeTimeZone={storeTimeZone} />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                    {task.pogComplianceScore != null
                      ? `${Math.round(task.pogComplianceScore * 100)}%`
                      : 'N/A'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                    {task.osaComplianceScore != null
                      ? `${Math.round(task.osaComplianceScore * 100)}%`
                      : 'N/A'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        task.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {task.isActive ? 'Yes' : 'No'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
